import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useViews } from "@/hooks/useViews";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ContactFilters } from "@/types/contact";

// Pre-built filter presets
export interface FilterPreset {
  id: string;
  name: string;
  // Which left-column tab this preset lives under. `category` is required so a
  // future preset must be explicitly assigned to Call or Email at compile time.
  category: 'call' | 'email';
  filters: ContactFilters;
}

// Helper to create a unique ID for each condition/group
const uid = () => crypto.randomUUID();

export const FILTER_PRESETS: FilterPreset[] = [
  {
    id: 'prospects-ab',
    name: 'Prospects A & B',
    category: 'email',
    filters: {
      group_type: 'AND',
      groups: [
        {
          id: uid(),
          type: 'AND',
          conditions: [{ id: uid(), table: 'contacts', field: 'type', operator: 'eq', value: 'Prospect' }],
        },
        {
          id: uid(),
          type: 'OR',
          conditions: [
            { id: uid(), table: 'contacts', field: 'rank', operator: 'eq', value: 'A' },
            { id: uid(), table: 'contacts', field: 'rank', operator: 'eq', value: 'B' },
          ],
        },
      ],
    },
  },
  {
    id: 'prospects-not-contacted',
    name: 'Prospects Not Yet Contacted',
    category: 'email',
    filters: {
      group_type: 'AND',
      groups: [
        {
          id: uid(),
          type: 'AND',
          conditions: [
            { id: uid(), table: 'contacts', field: 'type', operator: 'eq', value: 'Prospect' },
            { id: uid(), table: 'contacts', field: 'status', operator: 'eq', value: 'Not Yet Contacted' },
          ],
        },
      ],
    },
  },
  {
    id: 'referrers-ab',
    name: 'Referrers A & B',
    category: 'email',
    filters: {
      group_type: 'AND',
      groups: [
        {
          id: uid(),
          type: 'AND',
          conditions: [{ id: uid(), table: 'contacts', field: 'type', operator: 'eq', value: 'Referrer' }],
        },
        {
          id: uid(),
          type: 'OR',
          conditions: [
            { id: uid(), table: 'contacts', field: 'rank', operator: 'eq', value: 'A' },
            { id: uid(), table: 'contacts', field: 'rank', operator: 'eq', value: 'B' },
          ],
        },
      ],
    },
  },
  {
    id: 'referrers-not-contacted',
    name: 'Referrers Not Yet Contacted',
    category: 'email',
    filters: {
      group_type: 'AND',
      groups: [
        {
          id: uid(),
          type: 'AND',
          conditions: [
            { id: uid(), table: 'contacts', field: 'type', operator: 'eq', value: 'Referrer' },
            { id: uid(), table: 'contacts', field: 'status', operator: 'eq', value: 'Not Yet Contacted' },
          ],
        },
      ],
    },
  },
  {
    id: 'referrers-all',
    name: 'Referrers All',
    category: 'email',
    filters: {
      group_type: 'AND',
      groups: [
        {
          id: uid(),
          type: 'AND',
          conditions: [{ id: uid(), table: 'contacts', field: 'type', operator: 'eq', value: 'Referrer' }],
        },
      ],
    },
  },
  {
    id: 'referrers-active',
    name: 'Referrers Active',
    category: 'email',
    filters: {
      group_type: 'AND',
      groups: [
        {
          id: uid(),
          type: 'AND',
          conditions: [
            { id: uid(), table: 'contacts', field: 'type', operator: 'eq', value: 'Referrer' },
            { id: uid(), table: 'contacts', field: 'status', operator: 'eq', value: 'Active' },
          ],
        },
      ],
    },
  },
  {
    id: 'clients',
    name: 'Clients',
    category: 'email',
    filters: {
      group_type: 'AND',
      groups: [
        {
          id: uid(),
          type: 'AND',
          conditions: [{ id: uid(), table: 'contacts', field: 'type', operator: 'eq', value: 'Client' }],
        },
      ],
    },
  },
  {
    // Daily driver. Membership = matches a Daily tier (see daily_tier_member): a past/today
    // scheduled uncompleted activity, OR a Referral prospect not yet contacted, OR Prospect
    // rank A/B, OR an Active Client who is due, OR a Referrer not yet contacted.
    // Ordered by call_priority tier (1 past-activity → 2 today-activity → 3 Referral-Prospect-NYC
    // → 4 Prospect-A → 5 Prospect-B → 6 Client-due → 7 Referrer-NYC), then by next_contact
    // within each tier.
    //
    // The due-date gate applies to CLIENTS ONLY (Active + next_contact today-or-past, NULL
    // counting as due). Prospects A/B are never date-gated — they are always on the list.
    // Referrer rank A/B are deliberately NOT tiers: referrer rotation lives only in the
    // "30 Day Remarketing" preset below.
    // Spec: docs/superpowers/specs/2026-07-14-daily-and-30day-filter-rework.md
    id: 'daily-calls',
    name: 'Daily',
    category: 'call',
    filters: {
      group_type: 'AND',
      groups: [
        {
          id: uid(),
          type: 'AND',
          conditions: [
            { id: uid(), table: 'contacts', field: 'daily_tier_member', operator: 'is_not_empty', value: '' },
          ],
        },
      ],
      sort: [
        { field: 'call_priority', direction: 'ASC' },
        // NULLS FIRST, not LAST. next_contact is set to call+30d by the
        // update_contact_last_contacted trigger, so a contact you have ALREADY CALLED carries a
        // real date while one you have NEVER called is NULL. With NULLS LAST, calling someone
        // PROMOTED them above everyone you had never spoken to — the rotation ran backwards and
        // the tail of each tier was unreachable. Never-contacted belongs at the top of its band.
        { field: 'next_contact', direction: 'ASC', nulls: 'first' },
      ],
    },
  },
  {
    // Contacts whose birthday falls in the next 3 days (today..today+3), so you can
    // call to wish them. Correlated: matches the birthday-labeled date on the SAME
    // important_dates row, recurring by month/day (year-agnostic, wraps Dec->Jan).
    // Spec: docs/superpowers/specs/2026-07-11-call-filters-and-rotation-design.md (S4.2/4.5)
    id: 'daily-birthday',
    name: 'Daily Birthday',
    category: 'call',
    filters: {
      group_type: 'AND',
      groups: [
        {
          id: uid(),
          type: 'AND',
          conditions: [
            {
              id: uid(),
              table: 'important_dates',
              field: 'date',
              operator: 'between',
              value: '', // Ignored for relative dates
              label: 'birthday', // exact, case-insensitive (matches "Birthday"/"birthday")
              dateMode: 'relative',
              relativeValue: 0,
              relativeUnit: 'days',
              relativeDirection: 'from_now',
              relativeValueEnd: 3,
              relativeDirectionEnd: 'from_now',
            },
          ],
        },
      ],
    },
  },
  {
    // 30 Day REMARKETING — the narrow, referrer-only calling campaign. This is the verbal half
    // of the advertising push that runs alongside the email campaign: you work referrers A then
    // B, bang it out, and you run out of them. Deliberately NOT universal — no prospects, no
    // clients, no vendors.
    //
    // Three parameters, per Ian:
    //   1. type = Referrer, rank A or B     (A's on top, B's below)
    //   2. status != Do Not Contact
    //   3. last contacted on or before 30 days ago
    //
    // B's are the on-the-fence relationships — usually early in the relationship, not yet
    // clearly an A or a C. Hence A's first, then B's.
    //
    // The 30-day gate is what makes this list DRAIN as you call through it and refill 30 days
    // later. Never-contacted referrers (last_contacted IS NULL) match too, so they appear here
    // as well as in Daily tier 7. That overlap is intentional — the lists are worked at
    // different times and answer different questions.
    // Spec: docs/superpowers/specs/2026-07-14-daily-and-30day-filter-rework.md
    id: '30-day-remarketing',
    name: '30 Day Remarketing',
    category: 'call',
    filters: {
      group_type: 'AND',
      groups: [
        {
          id: uid(),
          type: 'AND',
          conditions: [
            {
              id: uid(),
              table: 'contacts',
              field: 'type',
              operator: 'eq',
              value: 'Referrer',
            },
            {
              id: uid(),
              table: 'contacts',
              field: 'status',
              operator: 'neq',
              value: 'Do Not Contact',
            },
            {
              id: uid(),
              table: 'contacts',
              field: 'last_contacted',
              operator: 'lt',
              value: '', // Ignored for relative dates
              dateMode: 'relative',
              relativeValue: 30,
              relativeUnit: 'days',
              relativeDirection: 'ago',
            },
          ],
        },
        {
          // OR, not AND — a rank cannot be both A and B, so an AND group here matches nothing.
          id: uid(),
          type: 'OR',
          conditions: [
            { id: uid(), table: 'contacts', field: 'rank', operator: 'eq', value: 'A' },
            { id: uid(), table: 'contacts', field: 'rank', operator: 'eq', value: 'B' },
          ],
        },
      ],
      sort: [
        { field: 'rank', direction: 'ASC' },          // A's on top, B's below
        { field: 'last_contacted', direction: 'ASC' }, // longest-neglected first within a rank
      ],
    },
  },
];

type FilterTab = 'all' | 'call' | 'email' | 'saved';

const TABS: { key: FilterTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'call', label: 'Call' },
  { key: 'email', label: 'Email' },
  { key: 'saved', label: 'Custom' },
];

const itemClass =
  "text-sm py-1.5 px-2 rounded hover:bg-accent cursor-pointer truncate";
const sectionLabelClass =
  "px-2 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider";

export function FilterNavMenu() {
  const navigate = useNavigate();
  const { views, loading } = useViews();
  const [activeTab, setActiveTab] = useState<FilterTab>('all');

  const goPreset = (id: string) => navigate(`/contacts?preset=${id}`);
  const goSaved = (id: string) => navigate(`/filters/${id}`);

  const presetsForTab =
    activeTab === 'call' || activeTab === 'email'
      ? FILTER_PRESETS.filter((p) => p.category === activeTab)
      : FILTER_PRESETS;

  const savedList = loading ? (
    <div className="flex items-center py-3 px-2">
      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
    </div>
  ) : views.length === 0 ? (
    <div className="py-1.5 px-2 text-sm text-muted-foreground">No saved filters</div>
  ) : (
    views.map((view) => (
      <div key={view.id} onClick={() => goSaved(view.id)} className={itemClass}>
        {view.name}
      </div>
    ))
  );

  return (
    <div className="flex">
      {/* Left: category tabs */}
      <div className="w-24 shrink-0 space-y-0.5 border-r pr-2">
        {TABS.map((tab) => (
          <div
            key={tab.key}
            onMouseEnter={() => setActiveTab(tab.key)}
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              "text-sm py-1.5 px-2 rounded cursor-pointer",
              activeTab === tab.key ? "bg-accent font-medium" : "hover:bg-accent/60"
            )}
          >
            {tab.label}
          </div>
        ))}
      </div>

      {/* Right: scrollable list for the active tab */}
      <div className="w-56 pl-2 max-h-[264px] overflow-y-auto">
        {activeTab === 'saved' ? (
          savedList
        ) : activeTab === 'all' ? (
          <>
            <div className={sectionLabelClass}>Quick Filters</div>
            {FILTER_PRESETS.map((preset) => (
              <div key={preset.id} onClick={() => goPreset(preset.id)} className={itemClass}>
                {preset.name}
              </div>
            ))}
            <div className="my-1.5 border-t" />
            <div className={sectionLabelClass}>Custom Filters</div>
            {savedList}
          </>
        ) : (
          presetsForTab.map((preset) => (
            <div key={preset.id} onClick={() => goPreset(preset.id)} className={itemClass}>
              {preset.name}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
